import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";
import { ConversationFilters } from "@/components/conversation";
import { ConversationDetails } from "@/components/conversation/ConversationDetails";
import {
  ConversationChannelPill,
  ConversationTypePill,
  getConversationChannel,
  getConversationType,
} from "@/components/conversation/conversationListShared";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { useAllAgents } from "@/hooks/agent";
import { useConversations, useShowConversation } from "@/hooks/conversation/useConversations";
import { DEFAULT_CONVERSATION_TIME_RANGE } from "@/constants/conversationFilters";
import { ConversationChannel, ConversationType } from "@/types/conversation/conversation";
import { DEFAULT_BRANDING } from "@/types/branding";

interface Conversation {
  id: string;
  agent_id: string;
  agent_name: string;
  owner_id: string | null;
  owner_name: string | null;
  start_time: string;
  summary: string;
  channel: ConversationChannel;
  conversationType: ConversationType;
  branding: any | null;
}

export default function RecentConversations() {
  const { data: agentsData, isLoading: agentsLoading } = useAllAgents(500);
  const agents: any[] = useMemo(
    () => [...(agentsData?.allAgents ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [agentsData?.allAgents],
  );
  const agentsById = useMemo(() => new Map(agents.map((agent) => [agent.id, agent])), [agents]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>(DEFAULT_CONVERSATION_TIME_RANGE);
  const [selectedChannel, setSelectedChannel] = useState<ConversationChannel | null>(null);
  const [selectedType, setSelectedType] = useState<ConversationType | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { mutateAsync: showConversation } = useShowConversation();

  const agentIds = useMemo(() => {
    if (!selectedAgent) return [];
    return [selectedAgent];
  }, [selectedAgent]);

  const {
    data: conversationData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useConversations(agentIds, timeRange, false, {
    conversationType: selectedType ?? undefined,
    channel: selectedChannel ?? undefined,
    ownerId: selectedOwner ?? undefined,
  });

  // Transform the hook data to match the expected Conversation interface
  const filteredConversations = useMemo(() => {
    if (!conversationData?.pages) return [];
    return conversationData.pages
      .flatMap((page) => page.data)
      .map((conv: any) => ({
        id: conv.id,
        agent_id: conv.agent_id,
        agent_name: conv.agent?.name || conv.agent_id || "Unknown Agent",
        owner_id: agentsById.get(conv.agent_id)?.owner?.id || null,
        owner_name: agentsById.get(conv.agent_id)?.owner?.full_name || null,
        start_time: conv.details?.conversation_date || conv.details?.created_at || new Date().toISOString(),
        summary: conv.details?.summary || "",
        channel: getConversationChannel(conv),
        conversationType: getConversationType(conv),
        branding: agentsById.get(conv.agent_id)?.branding || conv.agent?.branding || null,
      }));
  }, [conversationData, agentsById]);

  const hasActiveFilters = Boolean(selectedAgent || selectedChannel || selectedType || selectedOwner);
  const clearAllFilters = () => {
    setSelectedAgent(null);
    setSelectedChannel(null);
    setSelectedType(null);
    setSelectedOwner(null);
  };

  const handleViewDetails = async (conversation: Conversation) => {
    // Find the original conversation data from the hook
    const originalConv = conversationData?.pages.flatMap((page) => page.data).find((conv) => conv.id === conversation.id);

    if (!originalConv) {
      toast.error("Conversation not found");
      return;
    }

    // Open dialog immediately with the original conversation data
    setSelectedConversation(originalConv);
    setIsDialogOpen(true);

    try {
      // Load full details/analysis in the background
      const { conversation: conv } = await showConversation(originalConv);
      setSelectedConversation(conv);
    } catch (e: any) {
      toast.error(e.message || "Failed to load conversation details");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px] w-full">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-genie-primary/10 flex items-center justify-center mr-4">
              <ListChecks className="h-5 w-5 text-genie-primary" />
            </div>
            <h1 className="text-3xl font-bold text-[#222B45]">Recent Conversations</h1>{" "}
          </div>
        </div>

        {agentsLoading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Loading agents...</p>
          </div>
        ) : (
          <>
            <ConversationFilters
              agents={agents}
              selectedAgent={selectedAgent}
              onSelectedAgentChange={setSelectedAgent}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
              showOwner
              selectedChannel={selectedChannel}
              onSelectedChannelChange={setSelectedChannel}
              selectedType={selectedType}
              onSelectedTypeChange={setSelectedType}
              selectedOwner={selectedOwner}
              onSelectedOwnerChange={setSelectedOwner}
              hasActiveFilters={hasActiveFilters}
              onClearAll={clearAllFilters}
            />

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-[#222B45]">Recent Conversations</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {isLoading ? (
                  <div className="p-6 text-center text-gray-500">Loading conversations...</div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">No conversations found</div>
                ) : (
                  <>
                    {filteredConversations.map((conversation) => (
                      <div key={conversation.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0 flex-1 flex items-center gap-3">
                            <span className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full">
                              <GenieAvatar
                                size={32}
                                color={conversation.branding?.primaryColor || DEFAULT_BRANDING.primaryColor}
                                secondaryColor={conversation.branding?.secondaryColor}
                                gradientEnabled={conversation.branding?.gradientEnabled}
                                gradientAngle={conversation.branding?.gradientAngle}
                                avatarBackgroundOpacity={conversation.branding?.avatarBackgroundOpacity}
                                customAvatarUrl={conversation.branding?.logoUrl || conversation.branding?.customAvatarUrl}
                                hasCustomBranding={Boolean(conversation.branding)}
                              />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-[#222B45] whitespace-nowrap">{conversation.agent_name}</h3>
                                {conversation.owner_name && (
                                  <span className="text-xs text-gray-500 whitespace-nowrap">by {conversation.owner_name}</span>
                                )}
                                <ConversationChannelPill channel={conversation.channel} />
                                <ConversationTypePill type={conversation.conversationType} />
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {format(new Date(conversation.start_time), "MMM d, yyyy")}
                                </span>
                              </div>
                              {conversation.summary && (
                                <p className="truncate text-sm text-gray-500 mt-1">{conversation.summary}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-blue-500 hover:text-blue-600"
                            onClick={() => handleViewDetails(conversation)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}

                    {hasNextPage && (
                      <div className="p-6 text-center border-t border-gray-200">
                        <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="w-full">
                          {isFetchingNextPage ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                              Loading more conversations...
                            </>
                          ) : (
                            "Load More Conversations"
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>{" "}
      {selectedConversation && (
        <ConversationDetails isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} conversation={selectedConversation} />
      )}
    </div>
  );
}
